import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown, FaSyncAlt } from 'react-icons/fa';

import { fetchGroceryOrdersByStore } from './groceryApi';
import { STORE_ORDER_CHANNELS, isRestaurantStoreOrderSource } from './menuChannels';
import { isOrderCreatedToday } from './storeOrderDateUtils';
import {
  getStatusMeta,
  resolveStatusKey,
  isTerminalStatus,
} from './ui/orderStatusFlow';
import './css/DineInActiveOrders.css';

const STATUS_ORDER = ['NEW', 'ACCEPTED', 'PREPARING', 'READY', 'OFD', 'DELIVERED', 'COMPLETED'];

function normalizeStoreOrderSource(order) {
  return String(order?.orderSource ?? order?.Order_Source ?? '')
    .trim()
    .toUpperCase();
}

function orderMatchesStoreChannel(order, channel) {
  const source = normalizeStoreOrderSource(order);
  if (channel === 'pos') return source === 'POS' || source === '';
  if (channel === 'myeats') return source === 'MYEATS';
  if (channel === 'restaurant') return isRestaurantStoreOrderSource(source);
  return true;
}

function rawStatus(order) {
  return order?.orderStatusCode ?? order?.orderStatus ?? order?.Order_Status_Code ?? '';
}

function activeOnly(list) {
  return (Array.isArray(list) ? list : []).filter(
    (o) => !isTerminalStatus(rawStatus(o)) && isOrderCreatedToday(o)
  );
}

function countByStatus(orders) {
  const counts = {};
  orders.forEach((o) => {
    const key = resolveStatusKey(rawStatus(o));
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

const StoreActiveOrders = ({ storeId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ pos: [], myeats: [], restaurant: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState(true);

  const openOrders = useCallback(
    (orderType, statusKey) => {
      const base = `/home?tab=orders&orderType=${orderType}`;
      navigate(statusKey ? `${base}&status=${statusKey}` : base);
    },
    [navigate]
  );

  const load = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setError('');
    try {
      const rows = await fetchGroceryOrdersByStore(storeId);
      const active = activeOnly(rows);
      setData({
        pos: active.filter((o) => orderMatchesStoreChannel(o, 'pos')),
        myeats: active.filter((o) => orderMatchesStoreChannel(o, 'myeats')),
        restaurant: active.filter((o) => orderMatchesStoreChannel(o, 'restaurant')),
      });
    } catch {
      setError('Could not load live order counts.');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!storeId) return undefined;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [storeId, load]);

  if (!storeId) return null;

  return (
    <div
      className={`dinein-orders-summary store-active-orders-summary${
        collapsed ? ' dinein-orders-summary--collapsed' : ''
      }`}
    >
      <div className="dinein-orders-summary__head">
        <button
          type="button"
          className="dinein-orders-summary__collapse"
          onClick={() => setCollapsed((value) => !value)}
          aria-expanded={!collapsed}
        >
          <span className="dinein-orders-summary__title">Today&rsquo;s active orders</span>
          <FaChevronDown
            className="dinein-orders-summary__collapse-icon"
            aria-hidden="true"
          />
        </button>
        <div className="dinein-orders-summary__head-actions">
          <button
            type="button"
            className="dinein-orders-summary__refresh"
            onClick={load}
            disabled={loading}
            aria-label="Refresh order counts"
            title="Refresh"
          >
            <FaSyncAlt className={loading ? 'is-spinning' : ''} />
          </button>
        </div>
      </div>

      <div className="dinein-orders-summary__channels">
        {STORE_ORDER_CHANNELS.map((ch) => {
          const orders = data[ch.key] || [];
          const counts = countByStatus(orders);
          const Icon = ch.icon;
          const statusKeys = STATUS_ORDER.filter((k) => counts[k]);
          return (
            <div key={ch.key} className="dinein-channel-card">
              <button
                type="button"
                className="dinein-channel-card__header"
                onClick={() => openOrders(ch.key)}
                title={`Open ${ch.label} orders`}
              >
                <Icon className="dinein-channel-card__icon" aria-hidden="true" />
                <span className="dinein-channel-card__name">{ch.label}</span>
                <span className="dinein-channel-card__total">{orders.length}</span>
              </button>
              <div className="dinein-channel-card__statuses">
                {statusKeys.length === 0 ? (
                  <span className="dinein-status-line dinein-status-line--empty">
                    No active orders
                  </span>
                ) : (
                  statusKeys.map((k) => {
                    const meta = getStatusMeta(k);
                    return (
                      <button
                        key={k}
                        type="button"
                        className="dinein-status-line"
                        onClick={() => openOrders(ch.key, k)}
                        title={`Open ${ch.label} – ${meta.label}`}
                      >
                        <span
                          className="dinein-status-line__dot"
                          style={{ backgroundColor: meta.color }}
                          aria-hidden="true"
                        />
                        <span className="dinein-status-line__label">{meta.label}</span>
                        <span
                          className="dinein-status-line__count"
                          style={{ color: meta.color, backgroundColor: meta.bg }}
                        >
                          {counts[k]}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      {error ? (
        <div className="dinein-orders-summary__error">{error}</div>
      ) : null}
    </div>
  );
};

export default StoreActiveOrders;
